// Mes Ventes - Historique et revenus
import { useState, useEffect, useMemo, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Euro, Package, TrendingUp, Calendar, ChevronRight, Download,
  CheckCircle, Clock, Truck, Star, Eye, BarChart3, Wallet, PlusCircle, ArrowLeft,
  X, MessageCircle, RefreshCw, User
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { useCurrentUser } from '../../hooks/useCurrentUser';
import { TicketModal } from '../../components/TicketModal';

function VenteCard({ vente, onClick }) {
  const statusConfig = {
    completed: { label: 'Finalisée', bgColor: 'bg-green-100', textColor: 'text-green-700', icon: CheckCircle },
    pending: { label: 'En cours', bgColor: 'bg-yellow-100', textColor: 'text-yellow-700', icon: Clock },
    shipping: { label: 'Expédition', bgColor: 'bg-blue-100', textColor: 'text-blue-700', icon: Truck },
  };

  const status = statusConfig[vente.status] || statusConfig.completed;
  const StatusIcon = status.icon;

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      onClick={onClick}
      className="bg-white rounded-xl border border-gray-200 p-4 hover:shadow-md transition-shadow cursor-pointer active:scale-[0.99]"
    >
      <div className="flex items-start gap-4">
        {/* Image */}
        <div className="w-16 h-16 bg-gray-100 rounded-lg overflow-hidden flex-shrink-0">
          {vente.image ? (
            <img src={vente.image} alt={vente.title} className="w-full h-full object-cover" />
          ) : (
            <div className="w-full h-full flex items-center justify-center">
              <Package size={20} className="text-gray-300" />
            </div>
          )}
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0">
              <h3 className="text-sm font-semibold text-gray-900 truncate">{vente.title}</h3>
              <p className="text-xs text-gray-500 mt-0.5">Vendu à {vente.buyer}</p>
            </div>
            <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold flex items-center gap-1 flex-shrink-0 ${status.bgColor} ${status.textColor}`}>
              <StatusIcon size={10} />
              {status.label}
            </span>
          </div>

          <div className="flex items-center gap-4 mt-2">
            <div>
              <p className="text-[10px] text-gray-400 uppercase tracking-wide">Prix</p>
              <p className="text-sm font-bold text-green-600">+{vente.price} €</p>
            </div>
            <div>
              <p className="text-[10px] text-gray-400 uppercase tracking-wide">Date</p>
              <p className="text-xs font-medium text-gray-700">{vente.date}</p>
            </div>
            {vente.rating && (
              <div>
                <p className="text-[10px] text-gray-400 uppercase tracking-wide">Avis</p>
                <p className="text-xs font-medium text-gray-700 flex items-center gap-1">
                  <Star size={12} className="text-yellow-500 fill-yellow-500" />
                  {vente.rating}
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </motion.div>
  );
}

export default function VentesPage({ onSelectTool }) {
  const { user } = useCurrentUser();
  const [ventes, setVentes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [periode, setPeriode] = useState('all');
  const [selectedVente, setSelectedVente] = useState(null);
  
  // Refs et états pour le scroll des filtres
  const scrollContainerRef = useRef(null);
  const [showScrollHint, setShowScrollHint] = useState(true);
  const [showTicketModal, setShowTicketModal] = useState(false);
  
  // Gérer le scroll des filtres
  const handleScroll = () => {
    if (scrollContainerRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = scrollContainerRef.current;
      setShowScrollHint(scrollLeft < scrollWidth - clientWidth - 10);
    }
  };
  
  // Contacter l'acheteur → Ouvrir le modal Ticket
  const handleContactBuyer = () => {
    setShowTicketModal(true);
  };
  
  const handleRelancerAnnonce = () => {
    if (onSelectTool) {
      setSelectedVente(null);
      onSelectTool('nouvelle_annonce', 'Nouvelle Annonce');
    }
  };

  // Données démo
  const DEMO_VENTES = [
    {
      id: '1',
      title: 'Meuleuse Makita 125mm',
      price: 45,
      buyer: 'Sophie M.',
      date: '15 mai 2026',
      status: 'completed',
      rating: 5,
      image: 'https://images.pexels.com/photos/13792037/pexels-photo-13792037.jpeg?auto=compress&w=400',
    },
    {
      id: '2',
      title: 'Lot de vis et chevilles',
      price: 25,
      buyer: 'Marc D.',
      date: '10 mai 2026',
      status: 'completed',
      rating: 4.5,
      image: null,
    },
    {
      id: '3',
      title: 'Échelle télescopique 4m',
      price: 120,
      buyer: 'Paul R.',
      date: '20 avril 2026',
      status: 'shipping',
      rating: null,
      image: 'https://images.unsplash.com/photo-1581683705068-ca8f49fc7f45?w=400',
    },
    {
      id: '4',
      title: 'Ponceuse orbitale',
      price: 65,
      buyer: 'Julie L.',
      date: '5 mars 2026',
      status: 'completed',
      rating: 5,
      image: null,
    },
  ];

  useEffect(() => {
    setTimeout(() => {
      // Toujours afficher les données démo pour la prévisualisation
      // TODO: Connecter à l'API backend pour les vraies ventes
      setVentes(DEMO_VENTES);
      setLoading(false);
    }, 300);
  }, [user]);

  // Filtrage par période
  const filteredVentes = useMemo(() => {
    if (periode === 'all') return ventes;
    
    const now = new Date();
    const currentYear = now.getFullYear();
    const currentMonth = now.getMonth();
    const currentQuarter = Math.floor(currentMonth / 3);
    
    return ventes.filter(v => {
      // Parse date format "15 mars 2024"
      const months = {
        'janvier': 0, 'février': 1, 'mars': 2, 'avril': 3, 'mai': 4, 'juin': 5,
        'juillet': 6, 'août': 7, 'septembre': 8, 'octobre': 9, 'novembre': 10, 'décembre': 11
      };
      const parts = v.date.split(' ');
      if (parts.length < 3) return true;
      const venteMonth = months[parts[1].toLowerCase()];
      const venteYear = parseInt(parts[2]);
      
      if (periode === 'year') {
        return venteYear === currentYear;
      } else if (periode === 'quarter') {
        const venteQuarter = Math.floor(venteMonth / 3);
        return venteYear === currentYear && venteQuarter === currentQuarter;
      } else if (periode === 'month') {
        return venteYear === currentYear && venteMonth === currentMonth;
      }
      return true;
    });
  }, [ventes, periode]);

  const totalRevenu = filteredVentes.reduce((acc, v) => acc + v.price, 0);
  const ventesCompletees = filteredVentes.filter(v => v.status === 'completed').length;
  const noteMoyenne = filteredVentes.filter(v => v.rating).reduce((acc, v, _, arr) => acc + v.rating / arr.length, 0) || 0;

  return (
    <div className="max-w-4xl mx-auto space-y-4 sm:space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4">
        <div className="flex items-center gap-3">
          {onSelectTool && (
            <button 
              onClick={() => onSelectTool('hub_marketplace', 'Marketplace')}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              title="Retour au tableau de bord"
            >
              <ArrowLeft size={20} className="text-gray-600" />
            </button>
          )}
          <div>
            <h1 className="text-[16px] sm:text-xl font-bold text-[#1e3a5f]">Mes Ventes</h1>
            <p className="text-xs sm:text-sm text-gray-500">{filteredVentes.length} transaction{filteredVentes.length > 1 ? 's' : ''}{periode !== 'all' && ' (filtrées)'}</p>
          </div>
        </div>
        <Button variant="outline" className="flex items-center gap-2 text-xs sm:text-sm h-9 sm:h-10">
          <Download size={14} className="sm:w-4 sm:h-4" />
          Exporter
        </Button>
      </div>

      {/* Filtres Mobile - Style premium avec scroll horizontal */}
      <div className="sm:hidden">
        <div className="relative flex items-center">
          <div 
            ref={scrollContainerRef}
            onScroll={handleScroll}
            className="flex gap-1.5 overflow-x-auto scrollbar-hide flex-1 pr-6"
            style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
          >
            {[
              { id: 'all', label: 'Tout' },
              { id: 'month', label: 'Ce mois' },
              { id: 'quarter', label: 'Ce trimestre' },
              { id: 'year', label: 'Cette année' },
            ].map((p) => (
              <button
                key={p.id}
                onClick={() => setPeriode(p.id)}
                data-testid={`filter-${p.id}`}
                className={`flex-shrink-0 px-3 py-2 rounded-xl text-xs font-semibold transition-all whitespace-nowrap ${
                  periode === p.id
                    ? 'bg-orange-500 text-white shadow-sm'
                    : 'bg-white text-gray-600 hover:bg-gray-50 border border-gray-200'
                }`}
              >
                {p.label}
              </button>
            ))}
          </div>
          
          {/* Flèche orange discrète */}
          {showScrollHint && (
            <div className="flex-shrink-0 flex items-center pointer-events-none">
              <ChevronRight size={18} className="text-orange-500" strokeWidth={2.5} />
            </div>
          )}
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
        <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-2xl p-4 sm:p-5 text-white">
          <div className="flex items-center gap-2 sm:gap-3 mb-2 sm:mb-3">
            <div className="w-8 h-8 sm:w-10 sm:h-10 bg-white/20 rounded-lg flex items-center justify-center">
              <Euro size={16} className="sm:w-5 sm:h-5" />
            </div>
          </div>
          <p className="text-xl sm:text-2xl font-bold">{totalRevenu} €</p>
          <p className="text-xs sm:text-sm text-white/80">Revenus totaux</p>
        </div>

        <div className="bg-white rounded-2xl p-4 sm:p-5 border border-gray-200">
          <div className="flex items-center gap-2 sm:gap-3 mb-2 sm:mb-3">
            <div className="w-8 h-8 sm:w-10 sm:h-10 bg-blue-100 rounded-lg flex items-center justify-center">
              <Package size={16} className="text-blue-600 sm:w-5 sm:h-5" />
            </div>
          </div>
          <p className="text-lg sm:text-xl font-semibold text-gray-900">{ventes.length}</p>
          <p className="text-xs sm:text-sm text-gray-500">Ventes totales</p>
        </div>

        <div className="bg-white rounded-2xl p-4 sm:p-5 border border-gray-200">
          <div className="flex items-center gap-2 sm:gap-3 mb-2 sm:mb-3">
            <div className="w-8 h-8 sm:w-10 sm:h-10 bg-purple-100 rounded-lg flex items-center justify-center">
              <CheckCircle size={16} className="text-purple-600 sm:w-5 sm:h-5" />
            </div>
          </div>
          <p className="text-lg sm:text-xl font-semibold text-gray-900">{ventesCompletees}</p>
          <p className="text-xs sm:text-sm text-gray-500">Finalisées</p>
        </div>

        <div className="bg-white rounded-2xl p-4 sm:p-5 border border-gray-200">
          <div className="flex items-center gap-2 sm:gap-3 mb-2 sm:mb-3">
            <div className="w-8 h-8 sm:w-10 sm:h-10 bg-yellow-100 rounded-lg flex items-center justify-center">
              <Star size={16} className="text-yellow-600 sm:w-5 sm:h-5" />
            </div>
          </div>
          <p className="text-lg sm:text-xl font-semibold text-gray-900">{noteMoyenne.toFixed(1)}/5</p>
          <p className="text-xs sm:text-sm text-gray-500">Note moyenne</p>
        </div>
      </div>

      {/* Filtre période - Desktop uniquement */}
      <div className="hidden sm:flex gap-2 overflow-x-auto pb-2">
        {[
          { id: 'all', label: 'Tout' },
          { id: 'month', label: 'Ce mois' },
          { id: 'quarter', label: 'Ce trimestre' },
          { id: 'year', label: 'Cette année' },
        ].map((p) => (
          <button
            key={p.id}
            onClick={() => setPeriode(p.id)}
            className={`px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-colors ${
              periode === p.id
                ? 'bg-orange-500 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            {p.label}
          </button>
        ))}
      </div>

      {/* Liste des ventes */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="w-10 h-10 border-4 border-orange-500/30 border-t-orange-500 rounded-full animate-spin" />
        </div>
      ) : filteredVentes.length === 0 ? (
        <div className="bg-white rounded-xl border border-gray-200 p-12 text-center">
          <Euro size={48} className="mx-auto text-gray-300 mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 mb-2">
            {periode !== 'all' ? 'Aucune vente sur cette période' : 'Aucune vente'}
          </h3>
          <p className="text-gray-500 mb-4">
            {periode !== 'all' 
              ? 'Essaie de sélectionner une autre période'
              : "Tu n'as pas encore réalisé de vente"
            }
          </p>
          {onSelectTool ? (
            <Button 
              onClick={() => onSelectTool('mes_annonces', 'Mes Annonces')}
              className="bg-orange-500 hover:bg-orange-600 text-white"
            >
              Gérer mes annonces
            </Button>
          ) : (
            <Button className="bg-orange-500 hover:bg-orange-600 text-white">
              Gérer mes annonces
            </Button>
          )}
        </div>
      ) : (
        <div className="space-y-4">
          {filteredVentes.map((vente) => (
            <VenteCard key={vente.id} vente={vente} onClick={() => setSelectedVente(vente)} />
          ))}
        </div>
      )}

      {/* Conseil */}
      <div className="bg-gradient-to-r from-orange-50 to-yellow-50 rounded-2xl p-6 border border-orange-100">
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 bg-orange-500 rounded-xl flex items-center justify-center flex-shrink-0">
            <TrendingUp size={24} className="text-white" />
          </div>
          <div>
            <h3 className="font-bold text-gray-900 mb-1">Augmente tes ventes</h3>
            <p className="text-sm text-gray-600">
              Réduis ton prix de 10-15% pour attirer plus d'acheteurs sur tes annonces les plus anciennes.
            </p>
          </div>
        </div>
      </div>

      {/* Modal Détails de la vente */}
      <AnimatePresence>
        {selectedVente && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 md:left-[288px] bg-black/50 flex items-center justify-center z-50 p-4"
            onClick={() => setSelectedVente(null)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-white rounded-2xl max-w-md w-full max-h-[90vh] overflow-y-auto shadow-xl"
            >
              <>
                {/* Header avec image */}
                <div className="relative">
                  <div className="h-48 bg-gray-100 rounded-t-2xl overflow-hidden">
                    {selectedVente.image ? (
                      <img 
                        src={selectedVente.image} 
                        alt={selectedVente.title} 
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <Package size={48} className="text-gray-300" />
                      </div>
                    )}
                  </div>
                  <button
                    onClick={() => setSelectedVente(null)}
                    className="absolute top-3 right-3 w-8 h-8 bg-white/90 backdrop-blur rounded-full flex items-center justify-center shadow-sm hover:bg-white transition-colors"
                    >
                      <X size={18} className="text-gray-600" />
                    </button>
                    {/* Badge statut */}
                    <div className="absolute bottom-3 left-3">
                      {(() => {
                        const statusConfig = {
                          completed: { label: 'Finalisée', bgColor: 'bg-green-500', icon: CheckCircle },
                          pending: { label: 'En cours', bgColor: 'bg-yellow-500', icon: Clock },
                          shipping: { label: 'Expédition', bgColor: 'bg-blue-500', icon: Truck },
                        };
                        const status = statusConfig[selectedVente.status] || statusConfig.completed;
                        const StatusIcon = status.icon;
                        return (
                          <span className={`px-3 py-1.5 rounded-full text-xs font-semibold text-white flex items-center gap-1.5 ${status.bgColor}`}>
                            <StatusIcon size={12} />
                            {status.label}
                          </span>
                        );
                      })()}
                    </div>
                  </div>

                  {/* Contenu */}
                  <div className="p-5">
                    <h2 className="text-lg font-semibold text-gray-900 mb-1">{selectedVente.title}</h2>
                    
                    {/* Prix */}
                    <div className="flex items-center gap-2 mb-4">
                      <span className="text-2xl font-bold text-green-600">+{selectedVente.price} €</span>
                      <span className="text-sm text-gray-400">encaissé</span>
                    </div>

                    {/* Infos acheteur */}
                    <div className="bg-gray-50 rounded-xl p-4 mb-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-orange-100 rounded-full flex items-center justify-center">
                          <User size={18} className="text-orange-600" />
                        </div>
                        <div>
                          <p className="text-xs text-gray-500">Vendu à</p>
                          <p className="font-medium text-gray-900">{selectedVente.buyer}</p>
                        </div>
                        {selectedVente.rating && (
                          <div className="ml-auto text-right">
                            <p className="text-xs text-gray-500">Avis laissé</p>
                            <p className="font-medium text-gray-900 flex items-center gap-1">
                              <Star size={14} className="text-yellow-500 fill-yellow-500" />
                              {selectedVente.rating}/5
                            </p>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Date */}
                    <div className="flex items-center gap-2 text-sm text-gray-500 mb-6">
                      <Calendar size={14} />
                      <span>Vente effectuée le {selectedVente.date}</span>
                    </div>

                    {/* Actions */}
                    <div className="space-y-3">
                      <Button
                        onClick={handleContactBuyer}
                        className="w-full bg-orange-500 hover:bg-orange-600 text-white flex items-center justify-center gap-2"
                      >
                        <MessageCircle size={16} />
                        Contacter l'acheteur
                      </Button>
                      <Button
                        onClick={handleRelancerAnnonce}
                        variant="outline"
                        className="w-full flex items-center justify-center gap-2"
                      >
                        <RefreshCw size={16} />
                        Relancer une annonce similaire
                      </Button>
                    </div>
                  </div>
              </>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Modal Ticket (composant réutilisable) */}
      <TicketModal
        isOpen={showTicketModal}
        onClose={() => { setShowTicketModal(false); setSelectedVente(null); }}
        recipientName={selectedVente?.buyer}
        recipientId={selectedVente?.id}
        type="seller"
        onSuccess={() => setSelectedVente(null)}
      />
    </div>
  );
}
